import { ChevronDownIcon, PaperclipIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";

export type QueueProps = ComponentProps<"div">;
export const Queue = ({ className, ...props }: QueueProps) => (
	<div
		className={cn(
			"flex flex-col gap-2 rounded-xl border border-border bg-background px-3 pt-2 pb-2 shadow-xs",
			className,
		)}
		{...props}
	/>
);

export type QueueSectionProps = ComponentProps<typeof Collapsible>;
export const QueueSection = ({ defaultOpen = true, ...props }: QueueSectionProps) => (
	<Collapsible defaultOpen={defaultOpen} {...props} />
);

export type QueueSectionTriggerProps = ComponentProps<"button">;
export const QueueSectionTrigger = ({ className, ...props }: QueueSectionTriggerProps) => (
	<CollapsibleTrigger asChild>
		<button
			className={cn(
				"group flex w-full items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-left font-medium text-muted-foreground text-sm transition-colors hover:bg-muted",
				className,
			)}
			type="button"
			{...props}
		/>
	</CollapsibleTrigger>
);

export type QueueSectionLabelProps = ComponentProps<"span"> & {
	count?: number;
	label: string;
	icon?: ReactNode;
};
export const QueueSectionLabel = ({ count, icon, label, className, ...props }: QueueSectionLabelProps) => (
	<span className={cn("flex items-center gap-2", className)} {...props}>
		<ChevronDownIcon className="size-4 transition-transform group-data-[state=closed]:-rotate-90" />
		{icon}
		<span>{count} {label}</span>
	</span>
);

export type QueueSectionContentProps = ComponentProps<typeof CollapsibleContent>;
export const QueueSectionContent = (props: QueueSectionContentProps) => (
	<CollapsibleContent {...props} />
);

export type QueueListProps = ComponentProps<"ul">;
export const QueueList = ({ className, ...props }: QueueListProps) => (
	<ul className={cn("max-h-40 overflow-y-auto", className)} {...props} />
);

export type QueueItemProps = ComponentProps<"li">;
export const QueueItem = ({ className, ...props }: QueueItemProps) => (
	<li
		className={cn(
			"group flex flex-col gap-1 rounded-md px-3 py-1 text-sm transition-colors hover:bg-muted",
			className,
		)}
		{...props}
	/>
);

export type QueueItemIndicatorProps = ComponentProps<"span"> & { completed?: boolean };
export const QueueItemIndicator = ({ completed = false, className, ...props }: QueueItemIndicatorProps) => (
	<span
		className={cn(
			"mt-0.5 inline-block size-2.5 rounded-full border",
			completed ? "border-muted-foreground/20 bg-muted-foreground/10" : "border-muted-foreground/50",
			className,
		)}
		{...props}
	/>
);

export type QueueItemContentProps = ComponentProps<"span"> & { completed?: boolean };
export const QueueItemContent = ({ completed = false, className, ...props }: QueueItemContentProps) => (
	<span
		className={cn(
			"line-clamp-1 grow break-words",
			completed ? "text-muted-foreground/50 line-through" : "text-muted-foreground",
			className,
		)}
		{...props}
	/>
);

export type QueueItemActionsProps = ComponentProps<"div">;
export const QueueItemActions = ({ className, ...props }: QueueItemActionsProps) => (
	<div className={cn("flex gap-1", className)} {...props} />
);

export type QueueItemActionProps = Omit<ComponentProps<typeof Button>, "variant" | "size">;
export const QueueItemAction = ({ className, ...props }: QueueItemActionProps) => (
	<Button
		className={cn("size-auto rounded p-1 text-muted-foreground can-hover:opacity-0 transition-opacity hover:bg-muted-foreground/10 hover:text-foreground group-hover:opacity-100", className)}
		size="icon"
		type="button"
		variant="ghost"
		{...props}
	/>
);

export type QueueItemFileProps = ComponentProps<"span">;
export const QueueItemFile = ({ children, className, ...props }: QueueItemFileProps) => (
	<span className={cn("flex items-center gap-1 rounded border bg-muted px-2 py-1 text-xs", className)} {...props}>
		<PaperclipIcon size={12} />
		<span className="max-w-[100px] truncate">{children}</span>
	</span>
);
